import styled from "@emotion/styled";
import { Form, Space, Tag, theme } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import type { FormItemProps } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { isMissingField } from "../utils/mapAnalysisToBooking";
import { fieldKeyToDomId } from "../utils/fieldLabels";

const FieldShell = styled.div<{ $borderColor: string; $background: string }>`
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  border-left: 3px solid ${({ $borderColor }) => $borderColor};
  background: ${({ $background }) => $background};
  min-width: 0;
  scroll-margin-top: 96px;
`;

const MISSING_HINT_KEYS: Record<string, string> = {
  "transport.transport_type": "bookingWizardV2.details.missingHints.transport.transport_type",
  "transport.booking_type": "bookingWizardV2.details.missingHints.transport.booking_type",
  "transport.service_level": "bookingWizardV2.details.missingHints.transport.service_level",
  "transport.booking_incoterm": "bookingWizardV2.details.missingHints.transport.booking_incoterm",
  "transport.origin_pickup_date": "bookingWizardV2.details.missingHints.transport.origin_pickup_date",
  "supplier.name": "bookingWizardV2.details.missingHints.supplier.name",
  "supplier.email": "bookingWizardV2.details.missingHints.supplier.email",
  "supplier.phone": "bookingWizardV2.details.missingHints.supplier.phone",
  "supplier.country": "bookingWizardV2.details.missingHints.supplier.country",
  "supplier.province": "bookingWizardV2.details.missingHints.supplier.province",
  "supplier.city": "bookingWizardV2.details.missingHints.supplier.city",
  "supplier.address": "bookingWizardV2.details.missingHints.supplier.address",
};

const getMissingHint = (fieldKey: string, t: ReturnType<typeof useTranslation>["t"]) => {
  const mapped = MISSING_HINT_KEYS[fieldKey];
  if (mapped) return t(mapped);
  return t("bookingWizardV2.details.missingHintDefault");
};

interface StatusFormFieldProps extends FormItemProps {
  fieldKey: string;
  missingFields: string[];
  label: ReactNode;
}

export const StatusFormField = ({
  fieldKey,
  missingFields,
  label,
  children,
  ...rest
}: StatusFormFieldProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const missing = isMissingField(missingFields, fieldKey);
  const hint = getMissingHint(fieldKey, t);

  return (
    <FieldShell
      id={fieldKeyToDomId(fieldKey)}
      $borderColor={missing ? token.colorWarning : token.colorSuccess}
      $background={missing ? token.colorWarningBg : token.colorFillAlter}
    >
      <Form.Item
        {...rest}
        label={
          <Space size={6} wrap>
            {label}
            {missing ? (
              <Tag color="warning" icon={<ExclamationCircleOutlined />} style={{ margin: 0 }}>
                {t("bookingWizardV2.details.fillIn")}
              </Tag>
            ) : (
              <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>
                {t("bookingWizardV2.details.fromInvoice")}
              </Tag>
            )}
          </Space>
        }
        validateStatus={missing ? "warning" : undefined}
        help={missing ? hint : undefined}
        style={{ marginBottom: 0, ...rest.style }}
      >
        {children}
      </Form.Item>
    </FieldShell>
  );
};
